
import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import { ArrowRight, Camera, CheckCircle, Clock, Download, MapPin, Shield, Sparkles, Users } from "lucide-react";
import ImageComparisonSlider from "@/components/ImageComparisonSlider";
import { isBrowser } from "@/utils/runtime";

const TocTocClientes1 = () => {
  // Set page title and meta tags for SEO
  useEffect(() => {
    if (!isBrowser) return;
    globalThis.document.title = "Toc Toc Fotografia | Fotografia Profissional para Imóveis";
    
    // Add meta description dynamically
    const metaDescription = globalThis.document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", 
        "Toc Toc Fotografia: Valorize seu imóvel com fotos profissionais. Agende online, receba em 24h e aumente o valor percebido do seu imóvel.");
    }

    // Set canonical URL
    let canonicalLink = globalThis.document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = globalThis.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      globalThis.document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://toctoc.com.br/toctocclientes1');
  }, []);

  // FAQ data structure
  const faqItems = [
    {
      category: "Agendamento e Cancelamento",
      items: [
        {
          question: "Como funciona o agendamento das sessões?",
          answer: "O agendamento é feito diretamente na plataforma Toc Toc. Você escolhe o melhor dia e horário disponível e nosso sistema cuida do restante, enviando um fotógrafo até o imóvel no horário combinado."
        },
        {
          question: "Posso cancelar o agendamento de fotos?",
          answer: "Sim, o cancelamento é gratuito se realizado com até 12 horas de antecedência. Caso o cancelamento ocorra em menos de 12 horas, poderá haver uma taxa, pois já alocamos o fotógrafo para sua sessão."
        },
        {
          question: "Preciso estar no imóvel durante uma sessão de fotos?",
          answer: "Não é obrigatório, mas recomendamos que alguém esteja presente para facilitar o acesso e atender possíveis ofertas do fotógrafo."
        }
      ]
    },
    {
      category: "Entrega e Uso das Fotos",
      items: [
        {
          question: "Quantas fotos receberei por imóvel?",
          answer: "Não há limite fixo de fotos! Garantimos no mínimo 4 fotos por cômodo, dependendo do tamanho e da necessidade do imóvel. O único limite é o tempo da sessão, aproveitado para capturar o máximo de ângulos e detalhes que valorizem o espaço."
        },
        {
          question: "Em quanto tempo receberei as fotos?",
          answer: "As fotos editadas estarão disponíveis em até 24 horas após a sessão, prontas para uso em portais, redes sociais e outros canais."
        },
        {
          question: "As fotos podem ser usadas em qualquer plataforma?",
          answer: "Sim, todas as fotos são entregues em alta qualidade e podem ser utilizadas em portais imobiliários, sites pessoais e redes sociais."
        }
      ]
    },
    {
      category: "Sessão de Fotos",
      items: [
        {
          question: "Posso solicitar fotos adicionais durante a sessão?",
          answer: "Sim, desde que não ultrapasse o tempo estipulado para sessão. Recomendamos solicitar no final para aproveitar ao máximo o tempo."
        },
        {
          question: "O que acontece se chover no dia da sessão?",
          answer: "Em casos de condições climáticas adversas, você pode reagendar a sessão previamente, sem custos adicionais (>12h*)."
        },
        {
          question: "Que tipos de fotos são feitas durante a sessão?",
          answer: "As sessões incluem fotos de todos os ambientes do imóvel, áreas externas privativas, fachada e anexos estratégicos que valorizam o espaço, dentro da sessão agendada."
        }
      ]
    },
    {
      category: "Serviços e Pagamentos",
      items: [
        {
          question: "Preciso pagar uma mensalidade para usar a plataforma?",
          answer: "Não, não há mensalidade. Você só paga pelas sessões de fotos que agenda."
        },
        {
          question: "Como são definidos os preços dos serviços?",
          answer: "O preço varia conforme o pacote escolhido, que considera o tamanho do imóvel e tempo reservado."
        },
        {
          question: "Como funciona o pagamento?",
          answer: "O pagamento é feito online, diretamente pela plataforma Toc Toc, no momento do agendamento."
        }
      ]
    },
    {
      category: "Qualidade e Garantias",
      items: [
        {
          question: "Como a Toc Toc garante a qualidade das fotos?",
          answer: "Trabalhamos com fotógrafos experientes e utilizamos tecnologia avançada de edição para entregar imagens que valorizem o imóvel e gerem impacto."
        }
      ]
    },
    {
      category: "Suporte e Atendimento",
      items: [
        {
          question: "Como posso entrar em contato com o suporte da Toc Toc?",
          answer: "Você pode entrar em contato diretamente pelo chat disponível na plataforma ou pelo nosso canal de atendimento por e-mail e whatsapp."
        },
        {
          question: "Posso cancelar minha assinatura a qualquer momento?",
          answer: "Como não há mensalidade, não é necessário cancelar. Você só utiliza e paga pelo serviço quando precisar."
        }
      ]
    },
    {
      category: "Extras e Diferenciais",
      items: [
        {
          question: "Preciso fornecer algo para sessão de fotos?",
          answer: "Apenas o acesso ao imóvel no dia agendado. Nossa equipe cuida do restante (deixar o imóvel limpo e bonito para uma boa apresentação)."
        },
        {
          question: "Quais são os benefícios de usar a Toc Toc?",
          answer: "Fotos de alta qualidade, entrega rápida em até 24 horas, agendamento fácil e valorização do seu imóvel, garantindo mais cliques e interesse dos compradores."
        }
      ]
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      text: "Com as fotos da Toc Toc, vimos um aumento nas visualizações dos anúncios e mais interações nas campanhas. A qualidade das imagens faz toda a diferença, e o tempo de resposta da equipe ajuda a manter o fluxo de novas campanhas sem atrasos.",
      author: "Lucas",
      role: "gestor de tráfego imobiliária"
    },
    {
      text: "Eu estava tentando vender minha casa há meses, tirando fotos com meu celular, mas nada atraía interesse. Depois que contratei a Toc Toc, tudo mudou. As fotos profissionais fizeram toda a diferença – atraíram mais interessados e consegui vender. Foi uma das melhores decisões.",
      author: "Ricardo",
      role: "proprietário de imóvel"
    },
    {
      text: "Minha rotina é focada em captar, apresentar e negociar, e perder tempo tirando fotos era uma dor de cabeça. Desde que comecei a usar a Toc Toc, meus imóveis chamam muito mais atenção. Agora, posso focar nas vendas e deixar a fotografia com quem entende do assunto.",
      author: "Ana",
      role: "corretora de imóveis"
    },
    {
      text: "Tentamos fazer as fotos internamente, mas a qualidade caía e ficava difícil manter um padrão. Com a Toc Toc, o processo é rápido e sem complicações, e nossa equipe não precisa se preocupar com a fotografia. Isso tem nos ajudado a reduzir o tempo de venda dos imóveis e a melhorar a percepção de valor dos clientes.",
      author: "Eduardo",
      role: "gerente de imobiliária"
    },
    {
      text: "Eu subestimei o impacto de uma boa foto. Com as fotos da Toc Toc, o imóvel parecia outro! A experiência foi simples e valeu cada centavo.",
      author: "Sofia",
      role: "proprietária de imóvel"
    },
    {
      text: "Sou investidor e sempre estou comprando e vendendo imóveis. Antes, cada corretor queria tirar suas próprias fotos, e eu tinha que abrir o imóvel várias vezes – na minha rotina, isso não dá! Resolvi o problema com a Toc Toc. Agora, posso passar as imagens prontas para qualquer corretor, e o imóvel está sempre bem-apresentado. Muito mais prático.",
      author: "Ricardo",
      role: "proprietário de imóvel"
    }
  ];

  // Before and After image pairs
  const beforeAfterImages = [
    {
      id: 1,
      before: "https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&q=80&w=800",
      after: "https://images.unsplash.com/photo-1600607687644-a7d14565d3d0?auto=format&fit=crop&q=80&w=800",
      title: "Sala de Estar",
      description: "Aumento da luminosidade e destaque para os detalhes arquitetônicos"
    },
    {
      id: 2,
      before: "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=800", 
      after: "https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?auto=format&fit=crop&q=80&w=800",
      title: "Cozinha",
      description: "Cores vibrantes e ângulos estratégicos que valorizam o espaço"
    },
    {
      id: 3,
      before: "https://images.unsplash.com/photo-1512918580421-b2feee3b85a6?auto=format&fit=crop&q=80&w=800",
      after: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?auto=format&fit=crop&q=80&w=800", 
      title: "Quarto",
      description: "Iluminação adequada e enquadramento que transmite aconchego"
    }
  ];

  // Service features
  const features = [
    {
      icon: <Clock className="h-6 w-6 text-[#FF5605]" />,
      title: "Entrega em 24 Horas",
      description: "Tenha suas fotos profissionais prontas para anunciar em até 24 horas. Nossa equipe trabalha rapidamente para que você aproveite o momento de interesse do cliente, sem atrasos."
    },
    {
      icon: <Camera className="h-6 w-6 text-[#FF5605]" />,
      title: "Qualidade Impecável",
      description: "Cada imagem é cuidadosamente editada para destacar o que seu imóvel tem de melhor, garantindo um padrão de qualidade consistente que reforça a percepção de valor."
    },
    {
      icon: <Download className="h-6 w-6 text-[#FF5605]" />,
      title: "Acesso Simplificado",
      description: "Com o painel da Toc Toc, suas fotos ficam organizadas e disponíveis para download imediato, de qualquer dispositivo e onde você precisar."
    },
    {
      icon: <Sparkles className="h-6 w-6 text-[#FF5605]" />,
      title: "Edição Inteligente com IA",
      description: "Nossas imagens passam por um processo de edição automatizado, destacando detalhes que fazem o cliente se conectar com o imóvel."
    },
    {
      icon: <Shield className="h-6 w-6 text-[#FF5605]" />,
      title: "Controle Total",
      description: "Agende facilmente, escolha os horários e gerencie suas sessões com controle total sobre a agenda, sem complicações."
    },
    {
      icon: <Users className="h-6 w-6 text-[#FF5605]" />,
      title: "Para Todos os Perfis",
      description: "Soluções para proprietários, corretores e imobiliárias, atendendo às necessidades específicas de cada um."
    }
  ];

  // Stats data
  const stats = [
    { value: "32%", label: "Mais Rápido", description: "Imóveis com boas fotos se destacam e fecham negócio mais rápido." },
    { value: "47%", label: "Maior Valor", description: "Fotos bem feitas elevam o valor percebido do imóvel – seu cuidado é visível." },
    { value: "12%", label: "Mais Cliques", description: "Anúncios com fotos atraentes recebem mais visualizações, porque capturam o interesse logo de cara." }
  ];

  // Profile benefits
  const profileBenefits = [
    {
      title: "Proprietários",
      description: "Aumente o valor percebido e atraia mais visitas com fotos profissionais que mostram seu imóvel de forma única.",
      icon: <Users className="h-10 w-10 text-[#FF5605]" />
    },
    {
      title: "Corretores",
      description: "Destaque-se no mercado com imagens que criam uma presença marcante e transmitem profissionalismo, gerando credibilidade e facilitando a venda.",
      icon: <CheckCircle className="h-10 w-10 text-[#FF5605]" />
    },
    {
      title: "Imobiliárias",
      description: "Fortaleça a identidade da sua marca com fotos impactantes que representam o melhor de cada imóvel, facilitando as vendas e consolidando a confiança dos clientes.",
      icon: <Shield className="h-10 w-10 text-[#FF5605]" />
    }
  ];

  // JSON-LD schema for better SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Toc Toc Fotografia Imobiliária",
    "description": "Serviço de fotografia profissional para imóveis, com agendamento online e entrega rápida em 24 horas.",
    "provider": {
      "@type": "Organization",
      "name": "Toc Toc",
      "url": "https://toctoc.com.br"
    },
    "areaServed": "Brasil",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Serviços de Fotografia Imobiliária",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Fotografia Profissional para Imóveis"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <TocTocHeader />
      
      {/* Adding JSON-LD schema for search engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      
      <main>
        {/* Hero Section - Pure White Background */}
        <section className="relative py-20 md:py-28 bg-white overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight text-toctoc-black">Destaque seu imóvel, desperte o interesse e encante o comprador.</h1>
                <p className="text-lg md:text-xl mb-4 text-toctoc-gray-medium">Fotos profissionais transformam o imóvel comum em um sonho de consumo – elevam o desejo, geram conexão.</p>
                <p className="text-lg mb-8 text-toctoc-gray-medium">A Toc Toc ajuda você a captar o melhor do imóvel, fazendo com que o potencial comprador não só veja o espaço, mas também se imagine vivendo ali.</p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="bg-[#FF5605] hover:bg-[#FF5605]/90 text-white border-0 rounded-full px-8">
                    Quero Valorizar Meu Imóvel
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
              <div className="relative hidden md:block">
                <div className="relative z-10 rounded-2xl overflow-hidden shadow-xl transform rotate-1 border-4 border-white">
                  <img 
                    src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=800" 
                    alt="Imóvel com fotografia profissional" 
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-4/5 rounded-2xl overflow-hidden shadow-xl -rotate-2 z-0 border-4 border-white">
                  <img 
                    src="https://images.unsplash.com/photo-1600607687126-8a3bbc994cec?auto=format&fit=crop&q=80&w=800" 
                    alt="Interior com fotografia profissional" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Benefits Section - Pure White Background */}
        <section className="py-20 bg-white" id="beneficios">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-toctoc-black">Venda Rápida e Com Maior Valor Percebido</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Fotos profissionais aumentam o valor percebido de qualquer imóvel, atraem mais visitas e ajudam você a se destacar na concorrência. Na Toc Toc, garantimos que cada imagem valorize o seu imóvel e mostre seu potencial para o comprador desde o primeiro olhar.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
              {profileBenefits.map((benefit, index) => (
                <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-white rounded-2xl overflow-hidden">
                  <CardContent className="p-8 flex flex-col items-center text-center">
                    <div className="bg-[#FF5605]/5 rounded-full w-20 h-20 flex items-center justify-center mb-6">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-toctoc-black">{benefit.title}</h3>
                    <p className="text-toctoc-gray-dark">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Differentials Section - Pure White Background */}
        <section className="py-20 bg-white" id="diferenciais">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-toctoc-black">A imagem certa faz o cliente imaginar a vida dele no seu imóvel</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Cada clique é pensado para fazer o cliente se ver no espaço – seja um comprador, um inquilino ou um hóspede. Quando a pessoa se sente conectada ao lugar, ela toma decisões rápidas e confiantes.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-white rounded-2xl overflow-hidden">
                  <CardContent className="p-8">
                    <div className="bg-[#FF5605]/5 rounded-full w-14 h-14 flex items-center justify-center mb-5">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-toctoc-black">{feature.title}</h3>
                    <p className="text-toctoc-gray-dark">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Stats Section - Pure White Background with Orange Accent */}
        <section className="py-20 bg-white text-toctoc-black" id="estatisticas">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Fotos que aumentam visualizações e aceleram negociações</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Imóveis com fotos profissionais se destacam, atraem mais cliques e têm um maior valor percebido, o que aumenta o interesse dos compradores e agiliza a venda.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center bg-white p-10 rounded-2xl shadow-md border-0 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-[#FF5605]"></div>
                  <p className="text-5xl font-bold mb-4 text-[#FF5605]">{stat.value}</p>
                  <p className="text-xl font-medium mb-3 text-toctoc-black">{stat.label}</p>
                  <p className="text-sm text-toctoc-gray-medium">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* How it Works Section - Pure White Background */}
        <section className="py-20 bg-white" id="como-funciona">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-toctoc-black">Como funciona</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Processo simples e eficiente para transformar a apresentação do seu imóvel
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center relative">
                <div className="bg-white border-2 border-[#FF5605] text-[#FF5605] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 relative z-10 shadow-md">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <div className="absolute top-8 left-1/2 w-full h-1 bg-gray-100 -z-0 hidden md:block"></div>
                <h3 className="text-xl font-bold mb-3 text-toctoc-black">Agende Online</h3>
                <p className="text-toctoc-gray-dark">Escolha o melhor dia e horário para a sessão de fotos – direto do seu celular, sem complicação.</p>
              </div>
              
              <div className="text-center relative">
                <div className="bg-white border-2 border-[#FF5605] text-[#FF5605] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 relative z-10 shadow-md">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <div className="absolute top-8 left-1/2 w-full h-1 bg-gray-100 -z-0 hidden md:block"></div>
                <h3 className="text-xl font-bold mb-3 text-toctoc-black">Fotógrafo no Local</h3>
                <p className="text-toctoc-gray-dark">No dia e hora agendados, o fotógrafo chega e faz a mágica acontecer, captando o melhor do imóvel.</p>
              </div>
              
              <div className="text-center relative">
                <div className="bg-white border-2 border-[#FF5605] text-[#FF5605] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 relative z-10 shadow-md">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <div className="absolute top-8 left-1/2 w-full h-1 bg-gray-100 -z-0 hidden md:block"></div>
                <h3 className="text-xl font-bold mb-3 text-toctoc-black">Fotos Editadas</h3>
                <p className="text-toctoc-gray-dark">Em até 24 horas, você recebe as fotos editadas, prontas para publicar e impressionar.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-white border-2 border-[#FF5605] text-[#FF5605] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 shadow-md">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-toctoc-black">Use Onde Quiser</h3>
                <p className="text-toctoc-gray-dark">Publique nas plataformas, sites e redes sociais – boas fotos falam por si mesmas.</p>
              </div>
            </div>
            
            <div className="text-center mt-14">
              <Button size="lg" className="bg-[#FF5605] hover:bg-[#FF5605]/90 text-white rounded-full px-8">
                Agendar Minha Sessão
              </Button>
            </div>
          </div>
        </section>
        
        {/* Testimonials Section - Pure White Background */}
        <section className="py-20 bg-white" id="depoimentos">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-toctoc-black">Veja como outros proprietários, corretores e imobiliárias se beneficiaram</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Histórias de sucesso que comprovam a eficácia das fotos profissionais na valorização dos imóveis
              </p>
            </div>
            
            <TestimonialCarousel testimonials={testimonials} autoSlideInterval={5000} />
          </div>
        </section>
        
        {/* FAQ Section - Pure White Background */}
        <section className="py-20 bg-white" id="faq">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-toctoc-black">Perguntas Frequentes</h2>
              <p className="text-lg text-toctoc-gray-dark max-w-3xl mx-auto">
                Tudo o que você precisa saber sobre nosso serviço de fotografia para imóveis
              </p>
            </div>
            
            {faqItems.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-10 bg-white p-6 rounded-2xl shadow-sm border-0">
                <h3 className="text-xl font-bold mb-5 text-[#3322FC]">{category.category}</h3>
                <Accordion type="single" collapsible className="w-full">
                  {category.items.map((item, itemIndex) => (
                    <AccordionItem key={itemIndex} value={`item-${categoryIndex}-${itemIndex}`} className="border-b border-gray-100">
                      <AccordionTrigger className="text-left text-base hover:no-underline text-toctoc-black py-4">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-toctoc-gray-dark">
                        <p>{item.answer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </section>
        
        {/* CTA Section - Pure White Background with Orange Accent */}
        <section className="py-16 bg-white relative overflow-hidden" id="cta">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#FF5605]"></div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-toctoc-black">Fotos que mostram o que o seu imóvel tem de especial</h2>
            <p className="text-lg mb-10 max-w-3xl mx-auto text-toctoc-gray-dark">
              A Toc Toc é a solução para quem quer que o imóvel tenha o destaque que merece, seja para venda, aluguel ou hospedagem. 
              Agende agora e tenha fotos que passam confiança e atraem interesse.
            </p>
            
            <Button size="lg" className="bg-[#FF5605] hover:bg-[#FF5605]/90 text-white px-8 py-6 text-lg rounded-full hover:scale-105 transition-all duration-300">
              Reservar Sessão de Fotos
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </main>
      
      <TocTocFooter />
    </div>
  );
};

export default TocTocClientes1;
